import CONSTS from "src/app/pages/CreatorDiscoveryTool/legacy/app/helpers/consts";
import METHODS from "src/app/pages/CreatorDiscoveryTool/legacy/app/helpers/methods";
import { wsAxiosPut } from "src/helpers/wsAxios";
import { API_URLS } from "src/utils/API_URLS";
import isRecord from "src/utils/isRecord";
import { showErrorToast } from "src/utils/methods";

const getQueryDataObject = (
  filters,
  page,
  sortBy,
  sortOrder = "desc",
  excludedUserIds = [],
) => {
  return {
    searchFilters: {
      ...METHODS.mapCreatorSearchToRequest(filters),
      excludedUserIds,
    },
    pagination: {
      page,
      onPage: CONSTS.audienceResultsPageSize,
      orderBy: sortBy || "velocityIndex",
      order: sortOrder,
    },
  };
};

const areFiltersEmpty = (filters) => {
  if (isRecord(filters)) {
    return (
      Object.values(filters).filter((filter) => {
        if (Array.isArray(filter)) {
          return filter.length > 0;
        } else if (
          typeof filter === "object" &&
          filter !== null &&
          "value" in filter
        ) {
          return filter.value !== "or";
        } else {
          return Boolean(filter);
        }
      }).length === 0
    );
  } else {
    return true;
  }
};

const getInitialFilters = () => ({
  chosenTopics: [],
  chosenUnifiedCategories: [],
  chosenBrands: [],
  audienceCredibilityScore: null,
  paidPostPerformanceMin: null,
  minimumEr: null,
  selectedAudienceCountries: [],
  selectedAudienceLanguages: [],
  audienceGender: null,
  audienceGenderDensity: null,
  audienceAgeFrom: null,
  audienceAgeTo: null,
  audienceAgeDensity: null,
  influencerSocialUsername: [],
  influencerGender: null,
  influencerAgeFrom: null,
  influencerAgeTo: null,
  selectedInfluencerCountries: [],
  selectedInfluencerCities: [],
  influencerFollowers: [],
  lastPostsWithin: null,
  searchContents: [],
  hashtagsOption: {
    value: "or",
  },
  socialPlatformOption: {
    value: "or",
  },
  contentsOption: {
    value: "or",
  },
  mentionsOption: {
    value: "or",
  },
  searchBioKeywordsOption: {
    value: "or",
  },
  aiTagsOption: {
    value: "or",
  },
  audienceInterestsOption: {
    value: "or",
  },
  audienceBrandAffinityOption: {
    value: "or",
  },
  searchHashtags: [],
  searchMentions: [],
  searchBioKeywords: [],
  chosenKeywords: [],
  creatorsAvailableInCrm: null,
  excludeCreatorsFromDatabase: false,
  hasContactData: false,
  selectedProviders: [],
  selectedCreatorTags: [],
  creatorTagsMatchType: { value: "or" },
});

const mapSortOptionsFromBackend = (sortOptions, intl) =>
  (sortOptions ?? []).map(({ key, label }) => ({
    uuid: key,
    name: intl.formatMessage({
      id: `front_sort_by_${key}`,
      defaultMessage: label,
    }),
  }));

const updateSearchFiltersInDB = (metaFieldUuid, metaValue, uuids) => {
  wsAxiosPut(API_URLS.updateProjectMetaValue, {
    metaFieldUuid,
    metaValue,
    uuids,
  });
};

const hasOnlyCreatorSearchTabParam = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const { search } = window.location;

  if (!search) {
    return false;
  }

  const searchParams = new URLSearchParams(search);
  const entries = Array.from(searchParams.entries());

  if (entries.length !== 1) {
    return false;
  }

  const [key, value] = entries[0];

  return key === "tab" && value === "creatorSearch";
};

function getCSFiltersCount(obj) {
  const OPERATORS = ["or", "and"];

  return Object.values(obj).filter((v) => {
    if (v == null) return false;
    if (typeof v === "boolean") return v;
    if (typeof v === "string") return v.trim() !== "";
    if (Array.isArray(v)) return v.length > 0;

    if (typeof v === "object") {
      if (
        "value" in v &&
        typeof v.value === "string" &&
        OPERATORS.includes(v.value.trim().toLowerCase())
      ) {
        return false;
      }
      return Object.keys(v).length > 0;
    }

    return true;
  }).length;
}

const handleSaveFiltersToDB = (
  filtersMetaFieldUuid,
  baseFilters,
  suggestedFilters,
  projectId,
  sortBy = null,
  sortOrder = "desc",
) => {
  if (!filtersMetaFieldUuid || !projectId) {
    return;
  }

  const areBaseFiltersEmpty = areFiltersEmpty(baseFilters);
  const areSuggestedFiltersEmpty = areFiltersEmpty(suggestedFilters);

  // Early return if both filters are empty
  if (areBaseFiltersEmpty && areSuggestedFiltersEmpty) {
    updateSearchFiltersInDB(filtersMetaFieldUuid, null, [projectId]);
    return;
  }

  const mappedBaseFilters = METHODS.mapCreatorSearchToRequest(baseFilters);

  const mappedSuggestedFilters =
    METHODS.mapCreatorSearchToRequest(suggestedFilters);

  const filtersObject = mappedBaseFilters
    ? { ...mappedBaseFilters, aiSuggestions: mappedSuggestedFilters }
    : { aiSuggestions: mappedSuggestedFilters };

  const withSorting = {
    ...filtersObject,
    sortBy,
    sortOrder,
  };

  const filtersToSave = JSON.stringify(withSorting);
  updateSearchFiltersInDB(filtersMetaFieldUuid, filtersToSave, [projectId]);
};

const handleMapFilters = (
  filters,
  audienceCountries,
  influencerCountries,
  creatorTags,
  audienceLanguages,
) => {
  try {
    const mappedFilters = METHODS.mapCreatorSearchDataToFilters(
      filters,
      audienceCountries,
      influencerCountries,
      creatorTags,
      audienceLanguages,
    );
    return mappedFilters;
  } catch (error) {
    showErrorToast();
    console.error("Error mapping filters: ", error);
  }
};

export {
  hasOnlyCreatorSearchTabParam,
  getQueryDataObject,
  areFiltersEmpty,
  getInitialFilters,
  mapSortOptionsFromBackend,
  getCSFiltersCount,
  handleSaveFiltersToDB,
  handleMapFilters,
};
